import { UUID } from '@cast/leadcast-types';
import {
  InspectionStatusCounts,
  ReplacementCompletedCounts,
  ReplacementDetailedCounts,
  ReplacementPlannedCounts,
  ReplacementRoeCounts,
  ReplacementTargetKPI,
  VerifiedMaterialsCounts,
} from '@cast/leadcast-types/src/replacements';
import {
  SubmitDraftPostReplacementInspectionBody,
  SubmitDraftPreReplacementInspectionBody,
  SubmitPostReplacementInspectionBody,
  SubmitPreReplacementInspectionBody,
  UpdateReplacementTarget,
} from '@cast/leadcast-types/src/replacements/requests';
import { GetReplacementTarget } from '@cast/leadcast-types/src/replacements/responses';
import { BaseService, BaseServiceConstructor } from 'api/services/base.service';
import { plainToInstance } from 'class-transformer';
import { ServiceDiameter } from 'types/models';
import { Material } from 'types/models/inventory';
import {
  PaginatedResponse,
  PostReplacementInspection,
  PreReplacementInspection,
  ReplacementContractAssignment,
  ReplacementPoolMapEntry,
  ReplacementPoolProperty,
  ReplacementReviewInspectionResponse,
} from 'types/models/replacements';
import { ReplacementInspectionStatuses } from 'types/models/replacements/ReplacementInspectionStatuses';

export class ReplacementService extends BaseService {
  constructor({ serviceUrls, token, tenantName }: BaseServiceConstructor) {
    super({ serviceUrls, token, tenantName });
  }

  async getMaterials(): Promise<Material[]> {
    const { data } = await this.publicHttpClient.get<Material[]>(`/materials`, {
      withCredentials: false,
    });
    return data?.map((mat) => plainToInstance(Material, mat)) ?? [];
  }

  async getServiceLineDiameters(): Promise<ServiceDiameter[]> {
    const { data } = await this.publicHttpClient.get<ServiceDiameter[]>(`/serviceDiameters`, {
      withCredentials: false,
    });
    return data;
  }

  async getPropertiesInReplacementPoolByPropertyId(
    propertyId: UUID
  ): Promise<ReplacementPoolProperty[]> {
    const { data } = await this.publicHttpClient.get<ReplacementPoolProperty[]>(
      `/replacement/pool/property/${propertyId}`,
      {
        withCredentials: false,
      }
    );
    return (
      data?.map((poolProperty) => plainToInstance(ReplacementPoolProperty, poolProperty)) ?? []
    );
  }

  async getReplacementPoolPropertyInspectionInfo(
    propertyContractAssignmentId: UUID
  ): Promise<ReplacementReviewInspectionResponse> {
    const { data } = await this.publicHttpClient.get<ReplacementReviewInspectionResponse>(
      `/replacement/pool/${propertyContractAssignmentId}/inspections`,
      {
        withCredentials: false,
      }
    );

    return data;
  }

  async getReplacementPool(
    params = null,
    cancelToken = null
  ): Promise<PaginatedResponse<ReplacementPoolProperty>> {
    const { data, pagination } = await this.publicHttpClient.get<ReplacementPoolProperty[]>(
      `/replacement/pool`,
      {
        params: params,
        withCredentials: false,
        paramsSerializer: { indexes: null },
        cancelToken: cancelToken,
      }
    );

    if (data) {
      return {
        data: data.map((poolProperty) => plainToInstance(ReplacementPoolProperty, poolProperty)),
        pagination: pagination,
      };
    }

    return {
      data: [],
      pagination: {
        prev: null,
        next: null,
        count: 0,
        limit: 0,
        last: 0,
      },
    };
  }

  async getReplacementMapDataSearch(
    params = null,
    abortSignal: AbortSignal
  ): Promise<ReplacementPoolMapEntry[]> {
    try {
      const { data } = await this.publicHttpClient.get<ReplacementPoolMapEntry[]>(
        `/replacement/pool/map`,
        {
          params,
          withCredentials: false,
          signal: abortSignal,
        }
      );
      return data;
    } catch (e) {
      return undefined;
    }
  }

  async getPreReplacementInspection(
    propertyContractAssignmentId: UUID
  ): Promise<PreReplacementInspection> {
    try {
      const { data } = await this.publicHttpClient.get<PreReplacementInspection>(
        `/properties/${propertyContractAssignmentId}/replacement/preReplacementInspection`,
        {
          withCredentials: false,
        }
      );
      return data;
    } catch (err) {
      if (err.status === 404) {
        return null;
      }
      throw err;
    }
  }

  async saveDraftPreReplacementInspection(
    propertyId: string,
    inspection: SubmitDraftPreReplacementInspectionBody
  ): Promise<void> {
    await this.publicHttpClient.post(
      `/replacement/${propertyId}/inspections/preReplacement/draft`,
      inspection,
      {
        withCredentials: false,
      }
    );
  }

  async submitPreReplacementInspection(
    propertyId: string,
    inspection: SubmitPreReplacementInspectionBody
  ): Promise<void> {
    await this.publicHttpClient.post(
      `/replacement/${propertyId}/inspections/preReplacement`,
      inspection,
      {
        withCredentials: false,
      }
    );
  }

  async getPostReplacementInspection(
    propertyContractAssignmentId: UUID
  ): Promise<PostReplacementInspection> {
    try {
      const { data } = await this.publicHttpClient.get<PostReplacementInspection>(
        `/properties/${propertyContractAssignmentId}/replacement/postReplacementInspection`,
        {
          withCredentials: false,
        }
      );
      return data;
    } catch (err) {
      if (err.status === 404) {
        return null;
      }
      throw err;
    }
  }

  async saveDraftPostReplacementInspection(
    propertyId: string,
    inspection: SubmitDraftPostReplacementInspectionBody
  ): Promise<void> {
    await this.publicHttpClient.post(
      `/replacement/${propertyId}/inspections/postReplacement/draft`,
      inspection,
      {
        withCredentials: false,
      }
    );
  }

  async submitPostReplacementInspection(
    propertyId: string,
    inspection: SubmitPostReplacementInspectionBody
  ): Promise<void> {
    await this.publicHttpClient.post(
      `/replacement/${propertyId}/inspections/postReplacement`,
      inspection,
      {
        withCredentials: false,
      }
    );
  }

  // reasonForInclusion is a user-given required input. Adding default 'Manually added' to support MVP1
  async postPropertyToReplacementPool(
    propertyId,
    contractId = null,
    scheduledDate = null,
    serviceLineSide = null,
    reasonForInclusion = 'Manually added'
  ): Promise<void> {
    await this.publicHttpClient.post(
      `/properties/${propertyId}/replacement/pool`,
      {
        reasonForInclusion,
        contractId,
        scheduledDate,
        serviceLineSide,
      },
      {
        withCredentials: false,
      }
    );
  }

  async patchPreReplacementInspection(
    contractAssignmentId,
    inspectionId,
    updates: { status; comment }
  ): Promise<any> {
    return await this.publicHttpClient.patch(
      `/replacement/${contractAssignmentId}/preReplacementInspection/${inspectionId}`,
      {
        ...updates,
      },
      {
        withCredentials: false,
      }
    );
  }

  async patchPostReplacementInspection(
    contractAssignmentId,
    inspectionId,
    updates: { status; comment }
  ): Promise<any> {
    return await this.publicHttpClient.patch(
      `/replacement/${contractAssignmentId}/postReplacementInspection/${inspectionId}`,
      {
        ...updates,
      },
      {
        withCredentials: false,
      }
    );
  }

  async updatePropertyContractAssignmentReplacementStatus(
    contractAssignmentId,
    updates: { status }
  ): Promise<any> {
    return await this.publicHttpClient.patch(
      `/replacement/pool/${contractAssignmentId}/status`,
      {
        ...updates,
      },
      {
        withCredentials: false,
      }
    );
  }

  async updateReplacementContractAssignment(
    propertyId,
    contractAssignmentId,
    updates: { contractId?; scheduledDate?; serviceLineSide?; replacementStatus? }
  ): Promise<void> {
    await this.publicHttpClient.patch(
      `/properties/${propertyId}/replacement/contracts/${contractAssignmentId}`,
      {
        ...updates,
      },
      {
        withCredentials: false,
      }
    );
  }

  async getPropertiesReplacementContractAssignments(
    propertyId,
    cancelToken = null
  ): Promise<ReplacementContractAssignment[]> {
    const { data } = await this.publicHttpClient.get<ReplacementContractAssignment[]>(
      `/properties/${propertyId}/replacement/contracts`,
      {
        withCredentials: false,
        cancelToken,
      }
    );
    return data;
  }

  async getReplacementStatusByPropertyId(
    propertyId,
    cancelToken = null
  ): Promise<ReplacementInspectionStatuses> {
    const { data } = await this.publicHttpClient.get<ReplacementInspectionStatuses>(
      `/properties/${propertyId}/replacement/status`,
      {
        withCredentials: false,
        cancelToken,
      }
    );
    return data;
  }

  async updateManyReplacementContractAssignments(
    updates: {
      id: string;
      contractId?: string;
      contractName?: string;
      replacementScheduledDate?: string | Date;
      replacementStatus?: string;
      serviceLineSide?: 'Private' | 'Utility';
    }[]
  ): Promise<void> {
    await this.publicHttpClient.patch(`/replacement/properties/contracts`, updates, {
      withCredentials: false,
    });
  }

  // reasonForInclusion is a user-given required input. Adding default 'Manually added' to support MVP1
  async postPropertiesToReplacementPool(
    propertyIds,
    contractId,
    serviceLineSide,
    scheduledDate,
    reasonForInclusion = 'Manually added'
  ): Promise<void> {
    await this.publicHttpClient.post(
      `/replacement/pool`,
      {
        propertyIds,
        contractId,
        serviceLineSide,
        scheduledDate,
        reasonForInclusion,
      },
      {
        withCredentials: false,
      }
    );
  }

  async deleteSideFromReplacementPool(contractAssignmentId): Promise<void> {
    await this.publicHttpClient.delete(
      `/replacement/pool`,
      { contractAssignmentId },
      { withCredentials: false }
    );
  }

  async getReplacementStatuses(): Promise<any> {
    const { data } = await this.publicHttpClient.get(`/replacement/statuses`, {
      withCredentials: false,
    });
    return data;
  }

  async getReplacementContracts(): Promise<any> {
    const { data } = await this.publicHttpClient.get(`/replacement/contracts`, {
      withCredentials: false,
    });
    return data;
  }

  async getReplacementKpiTotals(params = null): Promise<ReplacementCompletedCounts> {
    const { data } = await this.publicHttpClient.get<ReplacementCompletedCounts>(
      `/replacement/kpis/counts/completed`,
      {
        withCredentials: false,
        params: params,
      }
    );
    return data;
  }

  async getReplacementKpiDetailedTotals(params = null): Promise<ReplacementDetailedCounts> {
    const { data } = await this.publicHttpClient.get<ReplacementDetailedCounts>(
      `/replacement/kpis/counts/details`,
      {
        withCredentials: false,
        params: params,
      }
    );
    return data;
  }

  async getReplacementPlannedCounts(params = null): Promise<ReplacementPlannedCounts> {
    const { data } = await this.publicHttpClient.get<ReplacementPlannedCounts>(
      `/replacement/kpis/counts/planned`,
      {
        withCredentials: false,
        params: params,
      }
    );
    return data;
  }

  async getReplacementKpiVerifications(params = null): Promise<VerifiedMaterialsCounts> {
    const { data } = await this.publicHttpClient.get<VerifiedMaterialsCounts>(
      `/replacement/kpis/counts/verifications`,
      {
        withCredentials: false,
        params: params,
      }
    );
    return data;
  }

  async getReplacementKpiInspections(params = null): Promise<InspectionStatusCounts> {
    const { data } = await this.publicHttpClient.get<InspectionStatusCounts>(
      `/replacement/kpis/counts/inspections`,
      {
        withCredentials: false,
        params: params,
      }
    );
    return data;
  }

  async getReplacementKpiRoeStatus(params = null): Promise<ReplacementRoeCounts> {
    const { data } = await this.publicHttpClient.get<ReplacementRoeCounts>(
      `/replacement/kpis/counts/roe`,
      {
        withCredentials: false,
        params: params,
      }
    );
    return data;
  }

  async getReplacementsPerformed(
    params = null
  ): Promise<Record<string, ReplacementDetailedCounts>> {
    const { data } = await this.publicHttpClient.get<Record<string, ReplacementDetailedCounts>>(
      `/replacement/kpis/counts/date`,
      {
        withCredentials: false,
        params: params,
      }
    );
    return data;
  }

  async getReplacementKpiTargets(): Promise<ReplacementTargetKPI> {
    const { data } = await this.publicHttpClient.get<ReplacementTargetKPI>(
      '/replacement/kpis/targets',
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async getReplacementTargets(): Promise<GetReplacementTarget[]> {
    const { data } = await this.publicHttpClient.get<GetReplacementTarget[]>(
      '/replacement/targets/year',
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async updateReplacementTargetYear(
    data: UpdateReplacementTarget
  ): Promise<GetReplacementTarget[]> {
    const { data: updatedTargets } = await this.publicHttpClient.put<
      GetReplacementTarget[],
      UpdateReplacementTarget
    >(`/replacement/targets/year`, data, {
      withCredentials: false,
    });
    return updatedTargets;
  }
}
